import React, { useCallback, useEffect, useState } from "react"
import { CoreTracking } from "../../../../models/CoreTracking"
import { useDispatch, useSelector } from "react-redux";
import ModalComponent from "../../../misc/ModalComponent"
import { Form, Row, Label, Col } from "reactstrap"
import SelectInput from "../../../misc/SelectInput"
import { EmptyRequestState, RequestState } from "../../../../models/RequestState"
import { GetOneRosterTrackingGroups, UpdateOneRosterTrackingGroup, ClearUpdateOneRosterTrackingGroup } from "../../../../store/OneRoster/OneRosterTrackingOperationAction"
import Toggle from "../../../misc/Toggle";
import { CoreOneRosterMappingTracking, OneRosterTrackingSearchRequest } from "../../../../models/CoreTrackingOperation";
import StatusCode from "../../../../util/StatusCode";
import Search from "../../../misc/Search";
import debounce from 'lodash/debounce';
import * as Helper from '../../../../util/Helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CoreStudentGroup } from "../../../../models/CoreGroup";
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RootState } from "../../../../store/store";



interface MapOneRosterTrackingGroupProps {
    tracking: CoreTracking
    close: (success: boolean) => void
}

const MapOneRosterTrackingGroup: React.FC<MapOneRosterTrackingGroupProps> = (props) => {
    const dispatch = useDispatch();
    const [search, setSearch] = useState<string>('');
    const [selectedGroup, setSelectedGroup] = useState<CoreOneRosterMappingTracking | undefined>(undefined)
    const oneRosterGroups = useSelector<RootState, RequestState<CoreOneRosterMappingTracking[]>>(state => state.oneRosterTrackingOperation.getOneRosterGroups)
    const [filter, setFilter] = useState<OneRosterTrackingSearchRequest>({ onlyInactive: true, search: '', selfObjectId: props.tracking.flowSyncId });
    const group = useSelector<RootState, RequestState<CoreStudentGroup>>(state => state.groups.group);
    const updatedGroup = useSelector<RootState, EmptyRequestState>(state => state.oneRosterTrackingOperation.updateOneRosterGroupTracking);

    const { t } = useTranslation(['common', 'schoolAdministration']);

    useEffect(() => {
        GetOneRosterTrackingGroups(filter)(dispatch);
    }, [dispatch, filter]);

    const close = (success: boolean) => {
        setSelectedGroup(undefined);
        if (updatedGroup.code !== StatusCode.NONE) {
            success = true;
            ClearUpdateOneRosterTrackingGroup()(dispatch);
        }
        props.close(success);
    }

    const submitInput: React.MouseEventHandler<HTMLButtonElement> = () => {
        if (selectedGroup?.trackingId) {
            UpdateOneRosterTrackingGroup(props.tracking.trackingId, selectedGroup.trackingId)(dispatch);
        }
    }

    const debouncedSearch = useCallback(
        debounce((search: string) => {
            setFilter({ ...filter, "search": search });
        }, 300), [filter]);


    const onSearch: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        e.preventDefault();
        const search = e.target.value;
        setSearch(search);
        debouncedSearch(search);
    }

    const updateOneRoster = (value: string | undefined) => {
        if (oneRosterGroups.code === StatusCode.COMPLETE) {
            var selectedGroup = oneRosterGroups.data.find(x => x.trackingId === value)
        }
        setSelectedGroup(selectedGroup);
    }

    const mapOneRosterGroups = () => {
        if (oneRosterGroups.code === StatusCode.COMPLETE) {
            return oneRosterGroups.data.map(x => ({ value: x.trackingId, text: `${x.displayName}, ${x.schoolUnitTitle}, (SIS-ID: ${x.sourceSystemId}) ${x.inactivated ? `, Inaktiverad` : ''}` }))
        }
        return [];
    }

    const count = oneRosterGroups.code === StatusCode.COMPLETE ?
        ((oneRosterGroups.data.length > 100) ? `(${t('trackingOperations.mapOneRosterTracking.firstHundredAnswers', { ns: 'schoolAdministration' })})`
            : (oneRosterGroups.data.length < 100) ? `(${oneRosterGroups.data.length})` : null)
        : null;

    return <ModalComponent
        size="xl"
        isOpen={true}
        toggleModal={() => close(false)}
        header={t('trackingOperations.mapOneRosterTracking.switch', { ns: 'schoolAdministration' })}
        update={submitInput}
        disabled={!selectedGroup || updatedGroup.code === StatusCode.COMPLETE}
        confirm={t('trackingOperations.mapOneRosterTracking.action.switch', { ns: 'schoolAdministration' })}
        saveStatus={updatedGroup.code}
        successMsg={t('trackingOperations.mapOneRosterTracking.success', { ns: 'schoolAdministration' })}
        cancel={t('action.cancel', { ns: 'common' })}>
        <h5>{t('trackingOperations.mapOneRosterTracking.group.title', { ns: 'schoolAdministration' })}</h5>
        <Row>
            <Col sm="8">
                <Search
                    value={search}
                    onChange={onSearch}
                    placeholder={t('trackingOperations.mapOneRosterTracking.group.search', { ns: 'schoolAdministration' })}
                />
            </Col>
            <Col sm="4">
                <Toggle
                    title={t('trackingOperations.mapOneRosterTracking.onlyInactive', { ns: 'schoolAdministration' })}
                    value={filter.onlyInactive}
                    id="onlyInactive"
                    disabled={oneRosterGroups.code !== StatusCode.COMPLETE}
                    update={(value) => setFilter({ ...filter, "onlyInactive": value })}
                />
            </Col>
        </Row>
        <Form>
            <Label>{t('trackingOperations.mapOneRosterTracking.group.select', { ns: 'schoolAdministration' })} {count}</Label>
            <SelectInput
                name="string"
                defaultText={t('trackingOperations.mapOneRosterTracking.group.select', { ns: 'schoolAdministration' })}
                selected={selectedGroup?.trackingId}
                disabled={oneRosterGroups.code !== StatusCode.COMPLETE}
                onChange={(name, value) => updateOneRoster(value)}
                items={mapOneRosterGroups()}
            />

            {selectedGroup?.trackingId && <>
                <hr />
                <h5>{t('trackingOperations.mapOneRosterTracking.switch', { ns: 'schoolAdministration' })}</h5>
                <p>{t('trackingOperations.mapOneRosterTracking.group.text', { ns: 'schoolAdministration' })}</p>
                <Row className="switch-oneRosterTrackings-row">
                    <Col sm="5" className="switch-oneRosterTrackings">
                        {group.code === StatusCode.COMPLETE && <>
                            <p><b>{t('trackingOperations.mapOneRosterTracking.headers.group', { ns: 'schoolAdministration' })}:</b> <Link to={`/group/${group.data.id}`} target="_blank">{group.data.title}</Link></p>
                            <p><b>{t('trackingOperations.mapOneRosterTracking.headers.class', { ns: 'schoolAdministration' })}:</b> <Link to={`/oneroster/class/${props.tracking.sourceSystemId}`} target="_blank">{group.data.title}</Link></p>
                            <p><b>{t('trackingOperations.mapOneRosterTracking.headers.school', { ns: 'schoolAdministration' })}:</b> {group.data.schoolUnit?.title}</p>
                            <p><b>{t('trackingOperations.mapOneRosterTracking.headers.sisId', { ns: 'schoolAdministration' })}: </b>{props.tracking.sourceSystemId}</p>
                        </>}
                    </Col>
                    <Col sm="2" className="icon-column">
                        <FontAwesomeIcon icon="shuffle"/>
                    </Col>
                    <Col sm="5" className="switch-oneRosterTrackings">
                        <p><b>{t('trackingOperations.mapOneRosterTracking.headers.group', { ns: 'schoolAdministration' })}:</b> <Link to={`/group/${selectedGroup.flowSyncId}`} target="_blank"> {selectedGroup.displayName}</Link></p>
                        <p><b>{t('trackingOperations.mapOneRosterTracking.headers.class', { ns: 'schoolAdministration' })}:</b> <Link to={`/oneroster/class/${selectedGroup.sourceSystemId}`} target="_blank"> {selectedGroup.displayName}</Link></p>
                        <p><b>{t('trackingOperations.mapOneRosterTracking.headers.school', { ns: 'schoolAdministration' })}:</b> {selectedGroup.schoolUnitTitle}</p>
                        <p><b>{t('trackingOperations.mapOneRosterTracking.headers.sisId', { ns: 'schoolAdministration' })}: </b>{selectedGroup.sourceSystemId}</p>
                        <p><b>{t('trackingOperations.mapOneRosterTracking.headers.inactivated', { ns: 'schoolAdministration' })}:</b> {Helper.formatDate(selectedGroup.inactivated)} </p>
                    </Col>
                </Row>
            </>
            }
        </Form>
    </ModalComponent>
}

export default MapOneRosterTrackingGroup;
